import React, { useState, useEffect } from "react";
import "../DepositStyle.css";
import config from '../config';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../contexts/ThemeContext';
import { AccountBalance, Person, Phone, AttachMoney, Payment, QrCode, AccountBalanceWallet } from '@mui/icons-material';

const Deposit = () => {    
  const user_id = Number(localStorage.getItem('userId'));
  const { isDarkMode } = useTheme();

  const [formData, setFormData] = useState({
    name: "",
    userId: "",
    phone: "",
    amount: "",
    paymentMode: "",
    remark: "",
    referenceNumber: "",
  });

  const [bankDetails, setBankDetails] = useState({
    bank: "",
    branch: "",
    accountHolderName: "",
    accountNumber: "",
    IFSC: "",
    aacountType: "",
    upiHandle: ""
  });

  const [showUPIDetails, setShowUPIDetails] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [amountInWords, setAmountInWords] = useState("");
  const [showUPIMode, setShowUPIMode] = useState(false);
  const [activeTab, setActiveTab] = useState('deposit'); // 'deposit' or 'payment'
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  const getUserDetails = async () => {
    try {  
      const response = await fetch(`${config.API_BASE_URL}getUserDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ user_id }),
      });
      
      const data = await response.json();
      
      setFormData(prevFormData => ({
        ...prevFormData,
        name: data.clientName,
        userId: data.clientId,
        phone: data.phoneNumber
      }));

      setBankDetails({
        bank: data.bank || "",
        branch: data.branch || "",
        accountHolderName: data.accountHolderName || "",
        accountNumber: data.accountNumber || "",
        IFSC: data.IFSC || "",
        aacountType: data.aacountType || "",
        upiHandle: data.upiHandle || ""
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Convert amount to words
    if (name === 'amount' && value) {
      const amount = parseFloat(value);
      if (!isNaN(amount)) {
        setAmountInWords(convertToWords(amount));
      } else {
        setAmountInWords("");
      }
    }
  };

  const convertToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    if (num === 0) return 'Zero';
    if (num < 0) return 'Negative ' + convertToWords(-num);

    let result = '';

    // Handle Crores
    if (num >= 10000000) {
      const crores = Math.floor(num / 10000000);
      result += convertToWords(crores) + ' Crore ';
      num %= 10000000;
    }

    // Handle Lakhs
    if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      result += convertToWords(lakhs) + ' Lakh ';
      num %= 100000;
    }

    // Handle Thousands
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      result += convertToWords(thousands) + ' Thousand ';
      num %= 1000;
    }

    // Handle Hundreds
    if (num >= 100) {
      result += ones[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }

    // Handle Tens and Ones
    if (num >= 20) {
      result += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    } else if (num >= 10) {
      result += teens[num - 10] + ' ';
      num = 0;
    }

    if (num > 0) {
      result += ones[num] + ' ';
    }

    return result.trim() + ' Rupees Only';
  };

  const handlePaymentMethodSelect = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!formData.paymentMode) {
      alert('Please select a payment mode');
      return;
    }

    setSelectedPaymentMethod(formData.paymentMode);
    setShowPaymentDetails(true);
  };

  const handleUPIPayment = () => {
    const upiId = bankDetails.upiHandle;
    if (!upiId) {
      alert('UPI ID not found. Please contact support.');
      return;
    }
    
    const amount = formData.amount;
    const message = `Deposit request for ₹${amount} - Client ID: ${formData.userId}`;
    const upiUrl = `upi://pay?pa=${upiId}&pn=${bankDetails.accountHolderName}&am=${amount}&cu=INR&tn=${encodeURIComponent(message)}`;
    
    // Try to open UPI app
    window.location.href = upiUrl;
    
    // Show payment completed option after a delay
    setTimeout(() => {
      const userConfirmed = window.confirm('Have you completed the UPI payment?');
      if (userConfirmed) {
        setPaymentCompleted(true);
      }
    }, 2000);
  };

  const handleBankTransferPayment = () => {
    setActiveTab('payment');
    alert('Please complete the bank transfer using the details shown below. After transfer, click "Payment Completed" button.');
  };

  const handlePaymentCompleted = () => {
    setPaymentCompleted(true);
    alert('Payment marked as completed. Please enter the transaction reference number and submit the request.');
  };

  const handleDepositRequest = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!formData.paymentMode) {
      alert('Please select a payment mode');
      return;
    }

    if (!paymentCompleted) {
      alert('Please complete the payment first before submitting the request.');
      return;
    }

    if (!formData.referenceNumber) {
      alert('Please enter the transaction reference number');
      return;
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}depositRequest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_id,
          amount: formData.amount,
          paymentMode: formData.paymentMode,
          remark: formData.remark,
          referenceNumber: formData.referenceNumber
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Deposit request submitted successfully');
        // Reset form
        setFormData(prev => ({
          ...prev,
          amount: '',
          paymentMode: '',
          remark: '',
          referenceNumber: ''
        }));
        setAmountInWords('');
        setShowPaymentDetails(false);
        setPaymentCompleted(false);
        setSelectedPaymentMethod('');
      } else {
        alert('Error: ' + (data.message || 'Failed to submit deposit request'));
      }
    } catch (error) {
      console.error('Error submitting deposit request:', error);
      alert('Error submitting deposit request');
    }
  };

  return (
    <div className={`deposit-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Header Section */}
      <div className="deposit-header">
        <h1 className="page-title">Deposit Request</h1>
        <p className="page-subtitle">Add funds to your trading account</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'deposit' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposit')}
        >
          <AttachMoney className="tab-icon" />
          Deposit Request
        </button>
        <button 
          className={`tab-button ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          <Payment className="tab-icon" />
          Payment Methods
        </button>
      </div>

      {/* Deposit Request Tab */}
      {activeTab === 'deposit' && (
        <div className="deposit-section">
          <div className="form-card">
            <div className="card-header">
              <h2 className="card-title">Deposit Request Form</h2>
            </div>

            <form className="deposit-form" onSubmit={(e) => e.preventDefault()}>
              {/* User Info Section */}
              <div className="form-section">
                <h3 className="section-title">Account Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <Person className="label-icon" />
                      Full Name
                    </label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      readOnly 
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <AccountBalance className="label-icon" />
                      User ID
                    </label>
                    <input 
                      type="text" 
                      name="userId" 
                      value={formData.userId} 
                      readOnly 
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <Phone className="label-icon" />
                      Phone Number
                    </label>
                    <input 
                      type="text" 
                      name="phone" 
                      value={formData.phone} 
                      readOnly 
                      className="form-input disabled"
                    />
                  </div>
                </div>
              </div>

              {/* Deposit Details Section */}
              <div className="form-section">
                <h3 className="section-title">Deposit Details</h3>
                <div className="form-group">
                  <label className="form-label">
                    <AttachMoney className="label-icon" />
                    Deposit Amount
                  </label>
                  <input 
                    type="number" 
                    name="amount" 
                    value={formData.amount} 
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter deposit amount"
                    min="1"
                    required
                  />
                  {amountInWords && (
                    <div className="amount-in-words">
                      <strong>Amount in words:</strong> {amountInWords}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Payment className="label-icon" />
                    Payment Mode
                  </label>
                  <select 
                    name="paymentMode" 
                    value={formData.paymentMode} 
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select Payment Mode</option>
                    <option value="UPI">UPI Payment</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Additional Remarks</label>
                  <textarea 
                    name="remark" 
                    value={formData.remark} 
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Enter any additional remarks (optional)"
                    rows="3"
                  />
                </div>
              </div>

              {/* Payment Method Selection */}
              {!showPaymentDetails && (
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handlePaymentMethodSelect}
                    className="submit-button"
                  >
                    Proceed to Payment
                  </button>
                </div>
              )}

              {/* Payment Details Section */}
              {showPaymentDetails && !paymentCompleted && (
                <div className="form-section">
                  <h3 className="section-title">Complete Payment</h3>
                  
                  {selectedPaymentMethod === 'UPI' && (
                    <div className="payment-method-card">
                      <div className="payment-info">
                        <h4>UPI Payment Details</h4>
                        <div className="info-item">
                          <span className="info-label">UPI ID:</span>
                          <span className="info-value">{bankDetails.upiHandle}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Amount:</span>
                          <span className="info-value">₹{formData.amount}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Client ID:</span>
                          <span className="info-value">{formData.userId}</span>
                        </div>
                      </div>
                      <div className="payment-actions">
                        <button 
                          type="button" 
                          onClick={handleUPIPayment}
                          className="payment-button upi-button"
                        >
                          Open UPI App
                        </button>
                        <button 
                          type="button" 
                          onClick={handlePaymentCompleted}
                          className="payment-button completed-button"
                        >
                          Payment Completed
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedPaymentMethod === 'BANK_TRANSFER' && (
                    <div className="payment-method-card">
                      <div className="payment-info">
                        <h4>Bank Transfer Details</h4>
                        <div className="info-item">
                          <span className="info-label">Bank Name:</span>
                          <span className="info-value">{bankDetails.bank}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Account Holder:</span>
                          <span className="info-value">{bankDetails.accountHolderName}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Account Number:</span>
                          <span className="info-value">{bankDetails.accountNumber}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">IFSC Code:</span>
                          <span className="info-value">{bankDetails.IFSC}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Amount:</span>
                          <span className="info-value">₹{formData.amount}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Client ID:</span>
                          <span className="info-value">{formData.userId}</span>
                        </div>
                      </div>
                      <div className="payment-actions">
                        <button 
                          type="button" 
                          onClick={handleBankTransferPayment}
                          className="payment-button bank-button"
                        >
                          View Bank Details
                        </button>
                        <button 
                          type="button" 
                          onClick={handlePaymentCompleted}
                          className="payment-button completed-button"
                        >
                          Payment Completed
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reference Number Section */}
              {paymentCompleted && (
                <div className="form-section">
                  <h3 className="section-title">Transaction Details</h3>
                  <div className="form-group">
                    <label className="form-label">Transaction Reference Number</label>
                    <input 
                      type="text" 
                      name="referenceNumber" 
                      value={formData.referenceNumber} 
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter transaction reference number"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Final Submit Button */}
              {paymentCompleted && (
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleDepositRequest}
                    className="submit-button"
                  >
                    Submit Deposit Request
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Payment Methods Tab */}
      {activeTab === 'payment' && (
        <div className="payment-section">
          <div className="form-card">
            <div className="card-header">
              <h2 className="card-title">Payment Methods</h2>
            </div>

            {/* Bank Transfer Section */}
            <div className="form-section">
              <h3 className="section-title">
                <AccountBalance className="section-icon" />
                Bank Transfer Details
              </h3>
              <div className="payment-method-card">
                <div className="payment-info">
                  <div className="info-item">
                    <span className="info-label">Bank Name:</span>
                    <span className="info-value">{bankDetails.bank || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Account Holder:</span>
                    <span className="info-value">{bankDetails.accountHolderName || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Account Number:</span>
                    <span className="info-value">{bankDetails.accountNumber || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">IFSC Code:</span>
                    <span className="info-value">{bankDetails.IFSC || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Branch:</span>
                    <span className="info-value">{bankDetails.branch || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Account Type:</span>
                    <span className="info-value">{bankDetails.aacountType || 'Not provided'}</span>
                  </div>
                </div>
                <div className="payment-instructions">
                  <h4>Bank Transfer Instructions:</h4>
                  <ul>
                    <li>Use the above bank details for NEFT/RTGS/IMPS transfers</li>
                    <li>Include your Client ID ({formData.userId}) in the transfer remarks</li>
                    <li>Keep the transaction reference number for verification</li>
                    <li>Funds will be credited within 1-2 business days</li>
                    <li>After transfer, contact support with transaction details</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* UPI Payment Section */}
            <div className="form-section">
              <h3 className="section-title">
                <QrCode className="section-icon" />
                UPI Payment
              </h3>
              <div className="payment-method-card">
                <div className="upi-section">
                  <div className="upi-info">
                    <div className="info-item">
                      <span className="info-label">UPI Handle:</span>
                      <span className="info-value">{bankDetails.upiHandle || 'Not provided'}</span>
                    </div>
                  </div>
                  {bankDetails.upiHandle && (
                    <div className="qr-code-section">
                      <div className="qr-code-container">
                        <QRCodeSVG 
                          value={bankDetails.upiHandle} 
                          size={200}
                          level="M"
                          includeMargin={true}
                        />
                      </div>
                      <p className="qr-instructions">Scan this QR code with your UPI app to make payment</p>
                    </div>
                  )}
                </div>
                <div className="payment-instructions">
                  <h4>UPI Instructions:</h4>
                  <ul>
                    <li>Scan the QR code with any UPI app (PhonePe, Google Pay, Paytm, etc.)</li>
                    <li>Enter the deposit amount</li>
                    <li>Add your Client ID ({formData.userId}) in the payment description</li>
                    <li>Complete the payment</li>
                    <li>Funds will be credited within 30 minutes</li>
                    <li>Keep the UPI transaction reference number</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3 className="section-title">
                <Phone className="section-icon" />
                Need Help?
              </h3>
              <div className="contact-info">
                <p>If you have any questions about deposits or payment methods, please contact our support team.</p>
                <div className="contact-buttons">
                  <button className="contact-button whatsapp">
                    <span>📱</span>
                    WhatsApp Support
                  </button>
                  <button className="contact-button call">
                    <span>📞</span>
                    Call Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;